/**
 * React Query hooks for Investor data
 */

import { queryKeys, useApiQuery, useApiMutation } from './index';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { investorService } from '@/services/investorService';
import type { InvestorEmailLogParams } from '@/types/investorEmail';
import type { InvestorExportLogParams } from '@/types/investorExport';
import type { IbanValidation } from '@/types/banking';
import { apiClient } from '@/utils/api-client';
import type {
  FundProxyVisibility,
  InvestorDetail,
  InvestorCreateData,
  InvestorUpdateData,
  InvestorContactCreateData,
  InvestorContactUpdateData,
  InvestorFilters,
  InvestorFundRelationshipBulkCreateData,
  InvestorFundRelationshipUpdateData,
  InvestorFundTransferRequest,
  InvestorFundTransferResponse,
  PaginatedInvestorSummary,
} from '@/types/investor';

type FundRelationship = InvestorDetail['fund_relationships'][number];

type TransferOptimisticContext = {
  previousFundInvestors?: InvestorDetail[];
  previousInvestorDetails: Array<{
    investorId: string;
    previousInvestorDetail?: InvestorDetail;
  }>;
};

const MONEY_ROUNDING_FACTOR = 100;
const FLOAT_EPSILON = 0.000001;

function roundMoney(value: number): number {
  return (
    Math.round((value + Number.EPSILON) * MONEY_ROUNDING_FACTOR) /
    MONEY_ROUNDING_FACTOR
  );
}

function parseMoney(value: string | number | null | undefined): number {
  if (typeof value === 'number') {
    return Number.isNaN(value) ? 0 : value;
  }

  if (typeof value !== 'string') {
    return 0;
  }

  const normalized = value.replaceAll(',', '').trim();
  if (!normalized) {
    return 0;
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function formatMoney(value: number): number {
  return roundMoney(Math.max(0, value));
}

function normalizeRelationshipStatus(
  targetStatus: 'active' | 'exited'
): FundRelationship['status'] {
  return targetStatus.toUpperCase() as FundRelationship['status'];
}

function calculateProRataTransferAmount({
  sourceTotal,
  transferRatio,
  isFullTransfer,
  capAmount,
}: {
  sourceTotal: number;
  transferRatio: number;
  isFullTransfer: boolean;
  capAmount?: number;
}): number {
  if (sourceTotal <= 0) {
    return 0;
  }

  let candidateAmount = isFullTransfer
    ? sourceTotal
    : roundMoney(sourceTotal * transferRatio);
  candidateAmount = Math.min(sourceTotal, candidateAmount);

  if (capAmount !== undefined) {
    candidateAmount = Math.min(candidateAmount, capAmount);
  }

  return roundMoney(Math.max(0, candidateAmount));
}

function findTransfereeRelationship(
  investorById: Map<string, InvestorDetail>,
  transfereeInvestorId: string,
  fundId: string
): FundRelationship | undefined {
  return investorById
    .get(transfereeInvestorId)
    ?.fund_relationships.find(
      (relationship) => relationship.fund_id === fundId
    );
}

function applyOptimisticTransferToFundInvestors(
  previousFundInvestors: InvestorDetail[],
  transferRequest: InvestorFundTransferRequest
): InvestorDetail[] {
  const nextFundInvestors = previousFundInvestors.map((investor) => ({
    ...investor,
    fund_relationships: investor.fund_relationships.map((relationship) => ({
      ...relationship,
    })),
  }));

  const investorById = new Map(
    nextFundInvestors.map((investor) => [investor.id, investor])
  );
  const transferorInvestor = investorById.get(
    transferRequest.transferor_investor_id
  );
  if (!transferorInvestor) {
    return previousFundInvestors;
  }

  const transferorRelationship = transferorInvestor.fund_relationships.find(
    (relationship) =>
      relationship.id === transferRequest.transferor_relationship_id &&
      relationship.fund_id === transferRequest.fund_id
  );
  if (!transferorRelationship) {
    return previousFundInvestors;
  }

  const allTransfereesResolvable = transferRequest.transferee_allocations.every(
    (allocation) =>
      findTransfereeRelationship(
        investorById,
        allocation.transferee_investor_id,
        transferRequest.fund_id
      )
  );
  if (!allTransfereesResolvable) {
    return previousFundInvestors;
  }

  let transferorCommitmentAmount = parseMoney(
    transferorRelationship.commitment_amount
  );
  let transferorTotalCalled = parseMoney(transferorRelationship.total_called);
  let transferorTotalContributed = parseMoney(
    transferorRelationship.total_contributed
  );
  let transferorTotalDistributed = parseMoney(
    transferorRelationship.total_distributed
  );

  for (const allocation of transferRequest.transferee_allocations) {
    const requestedTransferAmount = roundMoney(
      Math.max(0, allocation.transfer_commitment_amount || 0)
    );
    if (requestedTransferAmount <= 0 || transferorCommitmentAmount <= 0) {
      continue;
    }

    const transferAmount = Math.min(
      requestedTransferAmount,
      transferorCommitmentAmount
    );
    const isFullTransfer =
      Math.abs(transferAmount - transferorCommitmentAmount) <= FLOAT_EPSILON;
    const transferRatio = isFullTransfer
      ? 1
      : transferAmount / transferorCommitmentAmount;

    const transferredCalledAmount = calculateProRataTransferAmount({
      sourceTotal: transferorTotalCalled,
      transferRatio,
      isFullTransfer,
      capAmount: transferAmount,
    });
    const transferredContributedAmount = calculateProRataTransferAmount({
      sourceTotal: transferorTotalContributed,
      transferRatio,
      isFullTransfer,
    });
    const transferredDistributedAmount = calculateProRataTransferAmount({
      sourceTotal: transferorTotalDistributed,
      transferRatio,
      isFullTransfer,
    });

    const transfereeRelationship = findTransfereeRelationship(
      investorById,
      allocation.transferee_investor_id,
      transferRequest.fund_id
    );
    if (!transfereeRelationship) {
      continue;
    }

    transfereeRelationship.commitment_amount = formatMoney(
      parseMoney(transfereeRelationship.commitment_amount) + transferAmount
    );
    transfereeRelationship.total_called = formatMoney(
      parseMoney(transfereeRelationship.total_called) + transferredCalledAmount
    );
    transfereeRelationship.total_contributed = formatMoney(
      parseMoney(transfereeRelationship.total_contributed) +
        transferredContributedAmount
    );
    transfereeRelationship.total_distributed = formatMoney(
      parseMoney(transfereeRelationship.total_distributed) +
        transferredDistributedAmount
    );
    transfereeRelationship.status = normalizeRelationshipStatus('active');
    transfereeRelationship.exit_date = undefined;

    transferorCommitmentAmount = roundMoney(
      transferorCommitmentAmount - transferAmount
    );
    transferorTotalCalled = roundMoney(
      transferorTotalCalled - transferredCalledAmount
    );
    transferorTotalContributed = roundMoney(
      transferorTotalContributed - transferredContributedAmount
    );
    transferorTotalDistributed = roundMoney(
      transferorTotalDistributed - transferredDistributedAmount
    );
  }

  transferorRelationship.commitment_amount = formatMoney(
    transferorCommitmentAmount
  );
  transferorRelationship.total_called = formatMoney(transferorTotalCalled);
  transferorRelationship.total_contributed = formatMoney(
    transferorTotalContributed
  );
  transferorRelationship.total_distributed = formatMoney(
    transferorTotalDistributed
  );

  if (transferorCommitmentAmount <= FLOAT_EPSILON) {
    transferorRelationship.status = normalizeRelationshipStatus('exited');
    transferorRelationship.exit_date = transferRequest.effective_date;
  } else {
    transferorRelationship.status = normalizeRelationshipStatus('active');
    transferorRelationship.exit_date = undefined;
  }

  return nextFundInvestors;
}

function updateInvestorDetailFundRelationship(
  previousInvestorDetail: InvestorDetail,
  updatedFundInvestor: InvestorDetail,
  fundId: string
): InvestorDetail {
  const updatedRelationship = updatedFundInvestor.fund_relationships.find(
    (relationship) => relationship.fund_id === fundId
  );
  if (!updatedRelationship) {
    return previousInvestorDetail;
  }

  const hasFundRelationship = previousInvestorDetail.fund_relationships.some(
    (relationship) => relationship.fund_id === fundId
  );

  return {
    ...previousInvestorDetail,
    fund_relationships: hasFundRelationship
      ? previousInvestorDetail.fund_relationships.map((relationship) =>
          relationship.fund_id === fundId ? updatedRelationship : relationship
        )
      : [...previousInvestorDetail.fund_relationships, updatedRelationship],
  };
}

// Hook for fetching paginated investors list
export function useInvestors(
  filters?: InvestorFilters,
  options?: { page?: number; page_size?: number }
) {
  return useApiQuery<PaginatedInvestorSummary>(
    queryKeys.investors.list(filters, options),
    async () => {
      return investorService.getInvestors({
        page: options?.page || 1,
        page_size: options?.page_size || 25,
        filters: filters || {},
      });
    }
  );
}

// Hook for fetching a single investor
export function useInvestor(
  investorId: string | undefined,
  options?: {
    includeContacts?: boolean;
    includeFundRelationships?: boolean;
  }
) {
  return useApiQuery(
    queryKeys.investors.detail(investorId!),
    () =>
      investorService.getInvestor(investorId!, {
        include_contacts: options?.includeContacts ?? true,
        include_fund_relationships: options?.includeFundRelationships ?? true,
      }),
    {
      enabled: !!investorId,
    }
  );
}

// Hook for fetching a single investor contact
export function useInvestorContact(investorId: string, contactId: string) {
  return useApiQuery(
    queryKeys.investors.contact(investorId, contactId),
    () => investorService.getInvestorContact(investorId, contactId),
    {
      enabled: !!investorId && !!contactId && contactId !== 'new',
    }
  );
}

export function useInvestorContactSearch(params: {
  search: string;
  enabled?: boolean;
}) {
  return useApiQuery(
    queryKeys.investors.contactSearch(params.search),
    () => investorService.searchContacts({ q: params.search }),
    {
      enabled: params.enabled,
      staleTime: 60 * 1000,
    }
  );
}

// Hook for an investor's email log (business emails sent, with delivery status)
export function useInvestorEmails(
  investorId: string,
  params?: InvestorEmailLogParams
) {
  return useApiQuery(
    queryKeys.investors.emails(investorId, params),
    () => investorService.getInvestorEmails(investorId, params),
    {
      enabled: !!investorId,
      placeholderData: keepPreviousData,
    }
  );
}

// Hook for a single investor email, including its rendered body
export function useInvestorEmail(investorId: string, emailId: string) {
  return useApiQuery(
    queryKeys.investors.emailDetail(investorId, emailId),
    () => investorService.getInvestorEmail(investorId, emailId),
    {
      enabled: !!investorId && !!emailId,
    }
  );
}

export function useInvestorExportLogs(params: InvestorExportLogParams) {
  return useApiQuery(
    queryKeys.investors.exportLogs(params),
    () => investorService.getExportLogs(params),
    { placeholderData: keepPreviousData }
  );
}

export function useDownloadInvestorContactsExport() {
  return useApiMutation(
    (filters: InvestorFilters) =>
      investorService.downloadInvestorContactsExport(filters),
    {
      showSuccessToast: false, // the browser download is the feedback
      errorMessage: 'Export failed. Try again.',
    }
  );
}

// Hook for creating an investor contact
export function useCreateInvestorContact() {
  return useApiMutation(
    ({
      investorId,
      data,
    }: {
      investorId: string;
      data: InvestorContactCreateData;
    }) => investorService.createInvestorContact(investorId, data),
    {
      mutationKey: ['createInvestorContact'],
      invalidateQueries: [queryKeys.investors.all],
      successMessage: 'Contact created successfully',
    }
  );
}

// Hook for updating an investor contact
export function useUpdateInvestorContact() {
  return useApiMutation(
    ({
      contactId,
      data,
    }: {
      contactId: string;
      data: InvestorContactUpdateData;
    }) => investorService.updateInvestorContact(contactId, data),
    {
      mutationKey: ['updateInvestorContact'],
      invalidateQueries: [queryKeys.investors.all],
      successMessage: 'Contact updated successfully',
    }
  );
}

// Hook for deleting an investor contact
export function useDeleteInvestorContact() {
  return useApiMutation(
    (contactId: string) => investorService.deleteInvestorContact(contactId),
    {
      mutationKey: ['deleteInvestorContact'],
      invalidateQueries: [queryKeys.investors.all],
      successMessage: 'Contact deleted successfully',
    }
  );
}

// Hook for investor statistics
export function useInvestorStatistics() {
  return useApiQuery(queryKeys.investors.statistics(), () =>
    investorService.getInvestorStatistics()
  );
}

// Hook for investor investment overview
export function useInvestorInvestmentOverview(investorId: string) {
  return useApiQuery(
    queryKeys.investors.investmentOverview(investorId),
    () => investorService.getInvestorOverview(investorId),
    {
      enabled: !!investorId,
    }
  );
}

// Hook for fund-specific investors
export function useFundInvestors(
  fundId: string,
  options?: { enabled?: boolean }
) {
  return useApiQuery(
    queryKeys.investors.fundInvestors(fundId),
    () => investorService.getFundInvestors(fundId),
    {
      enabled:
        options?.enabled !== undefined ? options.enabled && !!fundId : !!fundId,
    }
  );
}

// Hook for fund-specific investor statistics
export function useFundInvestorStatistics(fundId: string) {
  return useApiQuery(
    queryKeys.investors.fundStatistics(fundId),
    () => investorService.getFundInvestorStatistics(fundId),
    {
      enabled: !!fundId,
    }
  );
}

// Hook for getting all funds' commitment totals (sum of investor commitments per fund)
export function useAllFundsCommitmentTotals() {
  return useApiQuery([...queryKeys.investors.all, 'commitment-totals'], () =>
    investorService.getAllFundsCommitmentTotals()
  );
}

// Hook for getting investors available to add to a fund (not already in the fund)
export function useAvailableInvestorsForFund(
  fundId: string,
  fundProxies: FundProxyVisibility = 'exclude'
) {
  return useApiQuery(
    [...queryKeys.investors.all, 'available-for-fund', fundId, fundProxies],
    () => investorService.getInvestorsAvailableForFund(fundId, fundProxies),
    { enabled: !!fundId }
  );
}

// Mutation hooks
export function useCreateInvestor() {
  return useApiMutation(
    (data: InvestorCreateData) => investorService.createInvestor(data),
    {
      mutationKey: ['createInvestor'],
      invalidateQueries: [
        queryKeys.investors.all,
        queryKeys.investors.statistics(),
      ],
      successMessage: 'Investor created successfully',
    }
  );
}

export function useUpdateInvestor() {
  const queryClient = useQueryClient();

  return useApiMutation(
    ({ id, data }: { id: string; data: InvestorUpdateData }) =>
      investorService.updateInvestor(id, data),
    {
      mutationKey: ['updateInvestor'],
      onMutate: async ({ id, data }) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries({
          queryKey: queryKeys.investors.detail(id),
        });

        // Snapshot the previous value
        const previousInvestor = queryClient.getQueryData<InvestorDetail>(
          queryKeys.investors.detail(id)
        );

        // Optimistically update to the new value
        if (previousInvestor) {
          queryClient.setQueryData(queryKeys.investors.detail(id), {
            ...previousInvestor,
            ...data,
          });
        }

        // Return a context object with the snapshotted value
        return { previousInvestor };
      },
      onError: (_err, variables, context) => {
        // If the mutation fails, use the context returned from onMutate to rollback
        if (context?.previousInvestor) {
          queryClient.setQueryData(
            queryKeys.investors.detail(variables.id),
            context.previousInvestor
          );
        }
      },
      invalidateQueries: [
        queryKeys.investors.all,
        queryKeys.investors.statistics(),
      ],
      successMessage: 'Investor updated successfully',
    }
  );
}

export function useDeleteInvestor() {
  return useApiMutation(
    (investorId: string) => investorService.deleteInvestor(investorId),
    {
      mutationKey: ['deleteInvestor'],
      invalidateQueries: [
        queryKeys.investors.all,
        queryKeys.investors.statistics(),
      ],

      successMessage: (res) => res.message,
    }
  );
}

export function useUnarchiveInvestor() {
  return useApiMutation(
    (investorId: string) => investorService.unarchiveInvestor(investorId),
    {
      mutationKey: ['unarchiveInvestor'],
      invalidateQueries: [
        queryKeys.investors.all,
        queryKeys.investors.statistics(),
      ],
      successMessage: 'Investor unarchived',
    }
  );
}

// Fund relationship mutations
export function useCreateFundRelationship() {
  return useApiMutation(
    (data: InvestorFundRelationshipBulkCreateData) =>
      investorService.createFundRelationship(data),
    {
      mutationKey: ['createFundRelationship'],
      invalidateQueries: [
        queryKeys.investors.all,
        queryKeys.funds.all, // Also invalidate funds as they may show investor counts
      ],
      successMessage: 'Investors added to fund successfully',
      onError: (error) => {
        if (error.message?.includes('already associated')) {
          return;
        }
      },
      errorMessage: 'Failed to add investors to fund',
    }
  );
}

export function useUpdateFundRelationship() {
  return useApiMutation(
    ({
      relationshipId,
      data,
    }: {
      relationshipId: string;
      data: InvestorFundRelationshipUpdateData;
    }) => investorService.updateFundRelationship(relationshipId, data),
    {
      mutationKey: ['updateFundRelationship'],
      invalidateQueries: [queryKeys.investors.all, queryKeys.funds.all],
      successMessage: 'Fund relationship updated successfully',
    }
  );
}

export function useDeleteFundRelationship() {
  return useApiMutation(
    (relationshipId: string) =>
      investorService.deleteFundRelationship(relationshipId),
    {
      mutationKey: ['deleteFundRelationship'],
      invalidateQueries: [queryKeys.investors.all, queryKeys.funds.all],
      successMessage: (response) => response.message,
      showErrorToast: true,
    }
  );
}

export function useTransferInvestorBetweenInvestors() {
  const queryClient = useQueryClient();

  return useApiMutation<
    InvestorFundTransferResponse,
    Error,
    InvestorFundTransferRequest,
    TransferOptimisticContext
  >((data) => investorService.transferInvestorBetweenInvestors(data), {
    mutationKey: ['transferInvestorBetweenInvestors'],
    onMutate: async (transferRequest) => {
      const fundInvestorsQueryKey = queryKeys.investors.fundInvestors(
        transferRequest.fund_id
      );
      const affectedInvestorIds = Array.from(
        new Set([
          transferRequest.transferor_investor_id,
          ...transferRequest.transferee_allocations.map(
            (allocation) => allocation.transferee_investor_id
          ),
        ])
      );

      await queryClient.cancelQueries({ queryKey: fundInvestorsQueryKey });
      await Promise.all(
        affectedInvestorIds.map((investorId) =>
          queryClient.cancelQueries({
            queryKey: queryKeys.investors.detail(investorId),
          })
        )
      );

      const previousFundInvestors = queryClient.getQueryData<InvestorDetail[]>(
        fundInvestorsQueryKey
      );
      const previousInvestorDetails = affectedInvestorIds.map((investorId) => ({
        investorId,
        previousInvestorDetail: queryClient.getQueryData<InvestorDetail>(
          queryKeys.investors.detail(investorId)
        ),
      }));

      if (previousFundInvestors) {
        const optimisticFundInvestors = applyOptimisticTransferToFundInvestors(
          previousFundInvestors,
          transferRequest
        );

        queryClient.setQueryData(
          fundInvestorsQueryKey,
          optimisticFundInvestors
        );

        const optimisticFundInvestorById = new Map(
          optimisticFundInvestors.map((investor) => [investor.id, investor])
        );
        for (const snapshot of previousInvestorDetails) {
          const previousInvestorDetail = snapshot.previousInvestorDetail;
          if (!previousInvestorDetail) {
            continue;
          }

          const optimisticFundInvestor = optimisticFundInvestorById.get(
            snapshot.investorId
          );
          if (!optimisticFundInvestor) {
            continue;
          }

          queryClient.setQueryData(
            queryKeys.investors.detail(snapshot.investorId),
            updateInvestorDetailFundRelationship(
              previousInvestorDetail,
              optimisticFundInvestor,
              transferRequest.fund_id
            )
          );
        }
      }

      return {
        previousFundInvestors,
        previousInvestorDetails,
      };
    },
    onError: (_error, transferRequest, context) => {
      const fundInvestorsQueryKey = queryKeys.investors.fundInvestors(
        transferRequest.fund_id
      );
      if (context?.previousFundInvestors) {
        queryClient.setQueryData(
          fundInvestorsQueryKey,
          context.previousFundInvestors
        );
      }

      for (const snapshot of context?.previousInvestorDetails ?? []) {
        if (!snapshot.previousInvestorDetail) {
          continue;
        }
        queryClient.setQueryData(
          queryKeys.investors.detail(snapshot.investorId),
          snapshot.previousInvestorDetail
        );
      }
    },
    // A transfer rewrites the leaver's drawdown notices, so the drawdown
    // totals and the fund transactions that carry them are now stale.
    invalidateQueries: [
      queryKeys.investors.all,
      queryKeys.funds.all,
      queryKeys.drawdowns.all,
      queryKeys.fundTransactions.all,
    ],
    successMessage: 'Investor transfer initiated successfully',
    showErrorToast: false,
  });
}

// IBAN validation hook
export function useValidateIban() {
  return useApiMutation(
    async ({ iban }: { iban: string }) => {
      return apiClient.post<IbanValidation>('/banking/validate-iban', {
        iban: iban.trim(),
      });
    },
    {
      mutationKey: ['validateIban'],
      showSuccessToast: false,
      showErrorToast: false,
    }
  );
}

// SWIFT/BIC validation hook (non-IBAN accounts: infers country + bank name)
export function useValidateSwift() {
  return useApiMutation(
    async ({ swift }: { swift: string }) => {
      return apiClient.post('/banking/validate-swift', { swift: swift.trim() });
    },
    {
      mutationKey: ['validateSwift'],
      showSuccessToast: false,
      showErrorToast: false,
    }
  );
}

// Combined hook for investors page with pagination
export function useInvestorsPageData(
  filters: InvestorFilters,
  pagination?: { page: number; page_size: number }
) {
  const isAllView = !filters?.fund;
  const { page = 1, page_size = 50 } = pagination || {};

  // Always call all hooks, but conditionally enable them
  const investorsQuery = useInvestors(filters, { page, page_size });
  const allStatsQuery = useInvestorStatistics();
  const fundStatsQuery = useFundInvestorStatistics(filters?.fund || '');
  const statsQuery = isAllView ? allStatsQuery : fundStatsQuery;

  return {
    investors: investorsQuery.data?.items || [],
    pagination: {
      total: investorsQuery.data?.total || 0,
      page: investorsQuery.data?.page || 1,
      page_size: investorsQuery.data?.page_size || page_size,
      total_pages: investorsQuery.data?.total_pages || 1,
    },
    statistics: statsQuery.data,
    isLoading: investorsQuery.isLoading || statsQuery.isLoading,
    isError: investorsQuery.isError || statsQuery.isError,
    error: investorsQuery.error || statsQuery.error,
    refetch: async () => {
      await Promise.all([investorsQuery.refetch(), statsQuery.refetch()]);
    },
  };
}
